import { useEffect, useMemo, useState } from "react";
import { useApiMutation } from "./use-api-mutation";
import { useDebounce } from "./use-debounce";

/**
 * Response type from the /check-url endpoint
 */
interface UrlCheckResult {
  server_ip?: string;
  status_code?: number;
  latency_ms?: number;
}

/**
 * Validation status for a URL
 */
type ValidationStatus = "idle" | "loading" | "valid" | "warning" | "invalid" | "error";

/**
 * Options for URL validation
 */
interface UseUrlValidationOptions {
  debounceMs?: number;
  enabled?: boolean;
}

/**
 * Return type of the useUrlValidation hook
 */
export interface UseUrlValidationReturn {
  status: ValidationStatus;
  isValid: boolean;
  isValidating: boolean;
  tooltipText: string;
  latency?: number;
  statusCode?: number;
}

/**
 * Custom hook for validating URLs with debouncing and visual feedback
 *
 * Validates URLs by calling the /check-url endpoint and provides
 * visual feedback (icon and tooltip) for the validation state.
 *
 * @param url - The URL to validate
 * @param options - Configuration options
 * @returns Validation state, icon, and tooltip text
 *
 * @example
 * const { isValid, icon, tooltipText } = useUrlValidation(url);
 */
export function useUrlValidation(
  url: string,
  options?: UseUrlValidationOptions,
): UseUrlValidationReturn {
  const { debounceMs = 600, enabled = true } = options || {};

  // Debounce the URL to avoid excessive API calls
  const debouncedUrl = useDebounce(url, debounceMs);

  // Track the last validated URL to prevent duplicate validations
  const [lastValidatedUrl, setLastValidatedUrl] = useState<string>("");

  // Set up the API mutation for checking URLs
  const mutation = useApiMutation<{ url: string }, UrlCheckResult>("POST", "/check-url");

  // Trigger validation when debounced URL changes
  useEffect(() => {
    // Only validate if:
    // 1. Validation is enabled
    // 2. URL is not empty
    // 3. URL is different from the last validated URL
    if (enabled && debouncedUrl && debouncedUrl !== lastValidatedUrl) {
      mutation.mutate({ url: debouncedUrl });
      setLastValidatedUrl(debouncedUrl);
    }
  }, [debouncedUrl, enabled, lastValidatedUrl, mutation.mutate]);

  // Reset validation state when URL is cleared
  useEffect(() => {
    if (!url) {
      setLastValidatedUrl("");
      mutation.reset();
    }
  }, [url, mutation.reset]);

  // Determine validation status based on mutation state and response
  const status: ValidationStatus = useMemo(() => {
    if (!url || !enabled) {
      return "idle";
    }

    if (mutation.isPending) {
      return "loading";
    }

    if (mutation.isError) {
      return "error";
    }

    if (mutation.isSuccess && mutation.data) {
      const { status_code } = mutation.data;

      // Valid if status code is 2xx or 3xx (redirects are fine)
      if (status_code && status_code >= 200 && status_code < 400) {
        return "valid";
      }

      // Warning if status code is 4xx (client errors, but URL is reachable)
      if (status_code && status_code >= 400 && status_code < 500) {
        return "warning";
      }

      // Invalid if status code is 5xx (server errors)
      if (status_code && status_code >= 500) {
        return "invalid";
      }

      // Error if no status code (request failed)
      return "error";
    }

    return "idle";
  }, [url, enabled, mutation.isPending, mutation.isError, mutation.isSuccess, mutation.data]);

  // Compute derived values
  const isValid = status === "valid" || status === "warning"; // Both valid and warning are acceptable
  const isValidating = status === "loading";
  const latency = mutation.data?.latency_ms;
  const statusCode = mutation.data?.status_code;

  // Generate tooltip text based on validation status
  const tooltipText = useMemo(() => {
    switch (status) {
      case "loading":
        return "Checking URL...";
      case "valid":
        return latency !== undefined ? `✓ Valid URL (${Math.round(latency)}ms)` : "✓ Valid URL";
      case "warning":
        return statusCode !== undefined
          ? `⚠ Warning: URL returned ${statusCode} (${latency !== undefined ? `${Math.round(latency)}ms` : ""})`
          : "⚠ Warning: Client error";
      case "invalid":
        return statusCode !== undefined
          ? `✗ Invalid URL (status: ${statusCode})`
          : "✗ URL check failed";
      case "error":
        return "✗ Could not reach URL";
      default:
        return "";
    }
  }, [status, latency, statusCode]);

  return {
    status,
    isValid,
    isValidating,
    tooltipText,
    latency,
    statusCode,
  };
}
